import { useTranslation } from 'react-i18next';
import { CHOICES, type ActiveQuestion, type Choice } from '@majority-report/shared';
import { findQuestion } from '../i18n/questionBank';

export interface ResolvedOption {
  choice: Choice;
  text: string;
}

export interface ResolvedQuestion {
  prompt: string;
  options: ResolvedOption[];
  /** Present only for player-written questions. */
  authorName: string | null;
}

/**
 * Turns whichever kind of question is live into text to render.
 *
 * Bank questions carry only an id and the two option keys drawn this round, so
 * the wording comes from the viewer's own locale — that is what lets an English
 * and a French player share a room and still answer the same question.
 * Player-written ones carry their text verbatim and are therefore the same for
 * everyone, in whatever language the author typed.
 */
export function useQuestionText(question: ActiveQuestion | null): ResolvedQuestion | null {
  const { i18n } = useTranslation();
  if (!question) return null;

  if (question.kind === 'custom') {
    return {
      prompt: question.prompt,
      options: question.options.map((text, index) => ({
        choice: CHOICES[index] as Choice,
        text,
      })),
      authorName: question.authorName,
    };
  }

  const language = i18n.resolvedLanguage ?? i18n.language;
  const entry = findQuestion(question.id, language);
  if (!entry) return null;

  // The letters are internal: side A is simply whichever key the server drew first.
  return {
    prompt: entry.prompt,
    options: question.optionKeys.map((key, index) => ({
      choice: CHOICES[index] as Choice,
      text: entry.options[key] ?? '',
    })),
    authorName: null,
  };
}
